// poiesis/src/views/bucket.ts
// Owner 2026-10-29: a reader switches a time chart's bucket (the site Overview's
// "Consumption and temperature" by the hour, not only by the day). WHICH grains,
// the default by the window's length and the point budget of one question are
// the server's grain registry, served on the kind's `granularity` structural row
// — this module only reads them against the window on screen. The engine
// buckets (time-bucket); nothing here aggregates.
//
// Owner 2026-10-30: "There should be no limit the FE just lazily loaded them."
// Every grain is offered for every window; a long one is read in chunks
// (views/chunks.ts), never refused.
import type { Granularity } from '../api/contract.generated'
import type { StructuralField, ViewKindSpec } from './catalog'

/** The kind's reader-switchable bucket row, or null (the kind has none). */
export function viewerBucketRow(spec: Pick<ViewKindSpec, 'structural_schema'> | undefined): StructuralField | null {
  const row = (spec?.structural_schema ?? []).find((r) => (r.viewer_choices?.length ?? 0) > 0)
  return row ?? null
}

const DAY_MS = 864e5
/** A grain's nominal length (ms) — for COUNTING buckets in a window, never for
 *  placing them (the engine lays buckets on the tenant's calendar). */
export const GRAIN_MS: Record<string, number> = {
  '15min': 9e5, hour: 36e5, day: DAY_MS, week: 7 * DAY_MS, month: 30.44 * DAY_MS, year: 365.25 * DAY_MS,
}
/** When the served row predates `points_per_request`: a month of hours. */
export const DEFAULT_POINTS_PER_REQUEST = 744

/** The window's length in days ([start, end), the page's period). */
export function windowDays(period: { period_start: string; period_end: string }): number {
  const ms = Date.parse(period.period_end) - Date.parse(period.period_start)
  return Number.isFinite(ms) && ms > 0 ? ms / DAY_MS : 0
}

/** The point budget of one chart question (served). */
export function pointsPerRequest(row: StructuralField | null | undefined): number {
  const n = row?.points_per_request
  return typeof n === 'number' && n > 0 ? n : DEFAULT_POINTS_PER_REQUEST
}

/** The grains the reader switches between — every one, for any window. */
export function bucketChoices(row: StructuralField): Granularity[] {
  return (row.viewer_choices ?? []) as Granularity[]
}

/** The served default for a window of `days` (the first span that holds it). */
export function defaultBySpan(row: StructuralField, days: number): Granularity | undefined {
  return (row.default_by_span ?? []).find(([max]) => max === null || days <= max)?.[1] as Granularity | undefined
}

/** The finest offered grain whose buckets over `days` fit ONE question's budget. */
export function finestWithinBudget(row: StructuralField, days: number): Granularity | undefined {
  const budget = pointsPerRequest(row)
  return bucketChoices(row).find((g) => GRAIN_MS[g] != null && (days * DAY_MS) / GRAIN_MS[g]! <= budget)
}

/** The bucket the chart draws and whether the reader pinned it. The reader's
 *  pick wins; else the widget's own pinned bucket; else AUTO: the served
 *  default for the page's window, and — zoomed in — the finest grain one
 *  question holds for the visible window (zooming in turns days into hours). */
export function effectiveBucket(row: StructuralField, days: number, picked: Granularity | null,
  pinned: Granularity | undefined, zoomed = false): { grain: Granularity; auto: boolean } {
  const offered = (g: string | null | undefined): g is Granularity =>
    g != null && (bucketChoices(row) as string[]).includes(g)
  if (offered(picked)) return { grain: picked, auto: false }
  if (offered(pinned)) return { grain: pinned, auto: false }
  const auto = (zoomed ? finestWithinBudget(row, days) : undefined) ?? defaultBySpan(row, days)
  return { grain: (offered(auto) ? auto : row.default) as Granularity, auto: true }
}
